import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'ncoded-component-library';
import { Field, useForm, useFormState } from 'react-final-form';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  getArrayLength,
  getCheckedValuesInObject,
  getUserFullName,
  normalizeEmail,
} from 'utils';
import InfiniteScroll from 'components/InfiniteScroll';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import VirtualArrayRender from 'components/VirtualArrayRender';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import { Contact } from 'models/Contact';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { SORT_VALUES } from '../InviteContactsForm/InviteContactsForm.component';

import './SelectContacts.styles.scss';

type SelectContactsProps = {
  className?: string;
  contacts: Contact[];
  sort: string;
  updateSort: (sort: string) => void;
  onContainerScroll: () => void;
};

const SelectContacts: React.FC<SelectContactsProps> = (props) => {
  const { className, contacts, sort, updateSort, onContainerScroll } = props;

  const [infiniteScroll, infiniteScrollRef] =
    useCallbackRef<InfiniteScrollRef>(null);

  const classes = classNames('select-contacts', className);

  const { t } = useTranslation();

  const {
    values: { contacts: formContacts },
  } = useFormState();
  const { change, batch } = useForm();

  const checkedContactsLength = useMemo(
    () => getArrayLength(getCheckedValuesInObject(formContacts || {})),
    [formContacts],
  );

  const areAllSelected = useMemo(
    () => checkedContactsLength === getArrayLength(contacts),
    [checkedContactsLength, contacts],
  );

  const onAllSelect = useCallback(
    () =>
      batch(() =>
        contacts.forEach(({ _id }) =>
          change(`contacts.${_id}`, !areAllSelected),
        ),
      ),
    [areAllSelected, contacts, batch, change],
  );

  const renderContact = useCallback(
    ({ _id, firstName, lastName, email, existUser }: Contact) => (
      <>
        <Field
          name={`contacts.${_id}`}
          type="checkbox"
          component={CheckboxField}
          label={
            <div className="name">
              {getUserFullName(firstName, lastName) || normalizeEmail(email)}
            </div>
          }
        />
        <div className={classNames('resolve-account', { approved: existUser })}>
          {existUser ? <CheckcircleIcon /> : <XcircleIcon />}
        </div>
      </>
    ),
    [],
  );

  return (
    <div className={classes}>
      <div className="header">
        <Checkbox checked={areAllSelected} onChange={onAllSelect} />
        <SortingButton
          content={t('contact')}
          sortingValue={SORT_VALUES.NAME}
          sort={sort}
          updateSort={updateSort}
        />
        <div className="resolve-account-header">{t('resolveAccount')}</div>
      </div>

      <InfiniteScroll
        ref={infiniteScrollRef}
        className="contacts"
        onScroll={onContainerScroll}
        localScroll
      >
        <VirtualArrayRender
          items={contacts}
          estimateSize={34}
          getScrollElement={() => infiniteScroll?.container}
          getItemKey={({ _id }) => _id}
          renderItem={(contact) => (
            <div className="contact-row">{renderContact(contact)}</div>
          )}
        />
      </InfiniteScroll>

      {!checkedContactsLength && (
        <Field
          name="contacts"
          hidden
          component="input"
          validate={() => t('requiredField')}
        />
      )}
    </div>
  );
};

export default SelectContacts;
